import { cloneDeep, isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, IWarning } from '@praktio/common-ui';
import { Helmet } from 'react-helmet';

import { getUserWithToken, registerUser } from '../../ducks/users';
import UnauthenticatedWrapper from '../common/UnauthenticatedWrapper/UnauthenticatedWrapper';
import CommonUser from '../common/User';
import { findFormErrors, handleUserFormEdit } from '../../utils/functions';
import { RootState } from '../../ducks/store';
import {
  GenericFormWarning,
  NetworkErrorFormWarning,
  ErrorCodes,
  InvalidPasswordFormWarning,
  SamePasswordFormWarning,
} from '../../utils/constants';

const useQuery = () => {
  const location = useLocation();
  return new URLSearchParams(location.search);
};

const Register = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const token = query.get('token') || undefined;
  const userId = query.get('userId') || undefined;

  const dispatch = useDispatch();
  useEffect(() => {
    if (userId && token) {
      dispatch(getUserWithToken(userId, token, navigate));
    }
  }, [dispatch, userId, token, navigate]);

  const { data } = useSelector((state: RootState) => {
    const data = state.users.current;
    return {
      data,
    };
  });

  const prevData = useRef<IUser | undefined>(data);
  const [user, setUser] = useState<IUser | undefined>(data);
  const [hasChanges, setHasChanges] = useState(!isEqual(user, prevData.current));
  const [disableSave, setDisableSave] = useState(true);
  const [userFormErrors, setUserFormErrors] = useState<IBootstrapFormErrors>();
  const [warnings, setWarnings] = useState<IWarning[]>([]);

  // This useEffect is triggered when the user data coming from the
  // API changes/updates, not to be confused with the useState user
  useEffect(() => {
    // If new data is coming in from the API and doesn't match
    // prevData, update the user state
    if (data && !isEqual(prevData.current, data)) {
      setUser(data);
      setHasChanges(false);
    }

    prevData.current = cloneDeep(data);
  }, [data, dispatch]);

  useEffect(() => {
    setDisableSave(!hasChanges);
  }, [hasChanges]);

  if (!user) {
    return (
      <div className="Register w-100 h-100">
        <Helmet>
          <title>Register - Praktio</title>
        </Helmet>
        <UnauthenticatedWrapper formName="" />
      </div>
    );
  }

  const prepAndExecuteUserFormEdit = (
    field: string,
    value: string | boolean | ICLEEntry | string[],
  ) => {
    const clonedUser = cloneDeep(user);
    const clonedFormErrors = cloneDeep(userFormErrors);
    const clonedWarnings = cloneDeep(warnings);

    handleUserFormEdit({ field, value, clonedUser, clonedFormErrors, clonedWarnings });

    const newHasChanges = !isEqual(clonedUser, prevData.current);

    // Queue/Execute setStates.
    setHasChanges(newHasChanges);
    setDisableSave(!newHasChanges);
    setUser(clonedUser);
    setUserFormErrors(clonedFormErrors);
    setWarnings(clonedWarnings);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasChanges || !user) {
      return;
    }
    setDisableSave(true);
    const newErrors = await findFormErrors({
      user: user,
      requirePassword: true,
      cleRequired: true,
      ignoreCurrentPassword: token ? true : false,
    });
    if (Object.keys(newErrors).length > 0) {
      if (!warnings.some((warning) => warning.title === GenericFormWarning.title)) {
        setWarnings([...warnings, GenericFormWarning]);
      }
      setUserFormErrors(newErrors);
    } else if (token) {
      try {
        await dispatch(registerUser(user, token, navigate));
      } catch (e) {
        const error = e as IError;
        const { message, errorCode } = error;
        let field: string | undefined;
        let newWarning = NetworkErrorFormWarning;
        switch (errorCode) {
          case ErrorCodes.InvalidPassword:
            field = 'currentPassword';
            newWarning = InvalidPasswordFormWarning;
            break;
          case ErrorCodes.SamePassword:
            field = 'password';
            newWarning = SamePasswordFormWarning;
            break;
          default:
            break;
        }

        if (field && newWarning) {
          setUserFormErrors({
            ...userFormErrors,
            [field]: message,
          });
        }

        setWarnings([newWarning]);
      }
    }

    setDisableSave(false);
  };

  return (
    <div className="Register w-100 h-100">
      <Helmet>
        <title>Register - Praktio</title>
      </Helmet>
      <UnauthenticatedWrapper formName="Register your account">
        {warnings &&
          warnings.length > 0 &&
          warnings.map((warning, i) => (
            <div key={`warning-${i}`} className="alert alert-danger" role="alert">
              <div className="alert-title">{warning.title}</div>
              <div className="alert-body">{warning.body}</div>
            </div>
          ))}
        <Form
          id="register-form"
          onSubmit={async (event) => {
            await handleSubmit(event);
          }}
        >
          <CommonUser
            handleFormEdit={prepAndExecuteUserFormEdit}
            user={user}
            userFormErrors={userFormErrors}
            cleRequired
            hideRoleSpecificFields
          />
          <div className="d-flex justify-content-between">
            <Button
              onClick={() => {}}
              form="register-form"
              type="submit"
              btnStyle="primary"
              className="form-submit-button"
              disabled={!!disableSave}
            >
              SUBMIT
            </Button>
            <div className="text-left">
              <span className="required-notice">* required</span>
            </div>
          </div>
        </Form>
      </UnauthenticatedWrapper>
    </div>
  );
};

export default Register;
